import type { BossDef } from './boss-base';
import type { BulletMotion } from '@/patterns/dsl';

/** Ember bomb (docs/02 B3 split): a slow fireball bursts into a ring of embers */
const EMBER_BOMB: BulletMotion = {
  accel: -60, minSpeed: 60,
  split: {
    after: 1.1, count: 7, spreadDeg: 360, speed: 150, radius: 9, life: 3,
    bullet: 'bullet/enemy/fire',
  },
};

/**
 * B6 Pyre Core — def + tuning (behaviour in pyre.ts). A fire core that
 * hangs like a small sun. Damage and time build HEAT (the body glows from
 * ember red to white-hot); at full heat a flame MANTLE swallows every hit,
 * it LEAPS and comes down as a meteor on a glowing scorch mark, then VENTS:
 * charcoal-dark, open vents take extra damage — and drifts home, its
 * burning wake streaking the floor from the crater. docs/02 Elementals.
 * Signatures: heat → mantle → meteor dive → vent (always), burning wake,
 * meteor fan (P1), ember bombs (P2), flame wall (P3).
 */
export const PYRE: BossDef = {
  id: 'pyre',
  nameKey: 'boss.pyre',
  tier: 3,
  minRoom: 3,
  baseHp: 1450,
  radius: 46,
  bodyKey: 'boss/pyre/body',
  movement: 'hover',
  contactDamage: 1,
  phases: [
    {
      hpPct: 1.0,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/fire', count: 3, spreadDeg: 22,
          interval: 2.6, telegraph: 0.45, speed: 230, radius: 11, damage: 1,
        },
      ],
    },
    {
      hpPct: 0.66,
      patterns: [
        {
          type: 'aimed', bullet: 'bullet/enemy/bomb', count: 1, spreadDeg: 0,
          interval: 3.0, telegraph: 0.5, speed: 190, radius: 13, damage: 1,
          motion: EMBER_BOMB,
        },
        {
          type: 'radial', bullet: 'bullet/enemy/fire', count: 8, interval: 3.8,
          spinDeg: 18, speed: 130, radius: 11, damage: 1, unlockTier: 1,
        },
      ],
    },
    {
      hpPct: 0.35,
      speedMul: 1.3,
      patterns: [
        {
          type: 'radial', bullet: 'bullet/enemy/fire', count: 10, interval: 3.2,
          spinDeg: 24, speed: 145, radius: 11, damage: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/fire', count: 5, spreadDeg: 40,
          interval: 2.8, telegraph: 0.45, speed: 255, radius: 11,
          damage: 1, unlockTier: 1,
        },
        {
          type: 'aimed', bullet: 'bullet/enemy/bomb', count: 1, spreadDeg: 0,
          interval: 3.6, telegraph: 0.5, speed: 190, radius: 13, damage: 1,
          motion: EMBER_BOMB, unlockTier: 2,
        },
      ],
    },
  ],
  evolve: [
    {
      // Ember pinwheel: a lazy single fire arm once the core is exposed
      tier: 2,
      fromPhase: 1,
      pattern: {
        type: 'spiral', bullet: 'bullet/enemy/fire', arms: 1, interval: 0.32,
        angularSpeed: 62, speed: 120, radius: 10, damage: 1,
      },
    },
    {
      // Flare burst: double meteor fan read from the swelling core
      tier: 3,
      pattern: {
        type: 'aimed', bullet: 'bullet/enemy/fire', count: 3, spreadDeg: 30,
        interval: 3.6, telegraph: 0.5, burst: 2, burstGap: 0.3,
        speed: 270, radius: 11, damage: 1,
      },
    },
  ],
};

/** Pyre tuning — heat cycle, meteor dive, wake, flame wall. */
export const PY = {
  /** Overheat after taking this share of max HP since the last vent */
  heatPerHp: 0.24,
  /** Heat per second on its own (× tier tempo) — it dives even if ignored */
  heatPassive: 0.03,
  /** Flame mantle boils up (immune) before the leap */
  mantleTime: 0.7,
  /** Lift-off (immune) → airborne (scorch mark glows) → slam */
  riseTime: 0.45,
  airTime: 0.45,
  fallTime: 0.3,
  /** Landing contact only in the last share of the fall */
  fallContact: 0.4,
  /** Vent window: charcoal body, open vents take extra damage */
  ventTime: 3.0,
  ventMul: 1.5,
  /** Impact: fire ring + a crater of burning patches */
  impactCount: 12,
  impactSpeed: 150,
  craterCount: 6,
  craterRadius: 78,
  craterLife: 4.5,
  markRadius: 76,
  /** Arena margin the meteor never lands inside (px past the wall band) */
  landInset: 120,
  /** Burning wake: patch drip while moving */
  trailInterval: 0.6,
  trailLife: 5.5,
  trailRadius: 26,
  trailSpeed: 4,
  trailMinSpeed: 40,
  /** Flame wall sweep below this hp share */
  wallHpPct: 0.35,
  wallInterval: 9.5,
  wallGap: 110,
  wallSpeed: 150,
  wallInset: 90,
  /** Body read-outs */
  hotTint: 0xffe6b0,
  ventTint: 0x6e5a58,
  mantleColor: 0xff9a3c,
  mantleCore: 0xfff1c9,
  markColor: 0xff7a30,
  markShadow: 0x3a1208,
  steamColor: 0xd8d0cc,
} as const;
